import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import { menuLabelClasses, menuSeparatorClasses } from '../styles';
import {
  controlChevron,
  controlFocus,
  controlHeight,
  controlHeightCompact,
  controlInvalid,
  controlSurface,
  controlSurfaceHover,
  controlTransition,
  controlValueText,
  controlValueTextCompact,
} from '../styles/control';

/**
 * Experimental Select styles, behind `release-experimental-design-system-forms`.
 * The listbox mirrors the menus' visual language but keeps its own recipes
 * (a listbox is not a menu); the trigger follows the `control` tier.
 */

/**
 * Each rule restates the one below it one condition heavier, so overlaps resolve on
 * specificity, not Tailwind's sort order: hover over open, invalid over both. Opening
 * never repaints validity.
 */
export const selectTriggerOpenClasses = [
  'data-[state=open]:border-control-border-focus',
  'data-[state=open]:ring-[1.5px]',
  'data-[state=open]:ring-control-border-focus/60',
  'enabled:hover:data-[state=open]:border-control-border-focus',
  'enabled:hover:data-[state=open]:ring-control-border-focus/60',
  "aria-[invalid='true']:data-[state=open]:border-control-border-invalid",
  "aria-[invalid='true']:data-[state=open]:ring-[1.5px]",
  "aria-[invalid='true']:data-[state=open]:ring-control-border-invalid/60",
  "aria-[invalid='true']:enabled:hover:data-[state=open]:border-control-border-invalid",
  "aria-[invalid='true']:enabled:hover:data-[state=open]:ring-control-border-invalid/60",
  // `[&>svg]` leads so `data-[state=open]` keys the trigger, not the glyph.
  '[&>svg]:data-[state=open]:text-control-icon',
].join(' ');

export const selectTriggerVariantsExperimental = cva(
  cn(
    'flex w-full items-center justify-between gap-x-2 disabled:cursor-not-allowed [&>span]:block [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-start disabled:[&>svg]:opacity-100 disabled:[&_button_svg]:text-current',
    controlTransition,
    // The trigger is itself disableable, so unlike Input it writes `disabled:`
    // variants inline.
    controlSurface,
    controlChevron,
    'disabled:border-control-border-disabled disabled:bg-control-background-disabled',
    // `data-[placeholder]`, not `::placeholder`, so this can't ride
    // `controlFieldText` — same tokens, spelled out. Weight included, or the
    // trigger's density variant is what sets it and compact re-bolds it.
    'disabled:text-control-text-value-disabled data-[placeholder]:font-normal data-[placeholder]:text-control-text-placeholder disabled:data-[placeholder]:text-control-text-placeholder-disabled [&_svg]:disabled:text-control-icon-disabled',
    "aria-[invalid='true']:data-[placeholder]:text-semantic-text-destructive",
    controlSurfaceHover,
    controlFocus,
    // After `controlFocus` so tailwind-merge keeps the destructive ring/border pair.
    controlInvalid,
    selectTriggerOpenClasses
  ),
  {
    variants: {
      density: {
        comfortable: cn(controlHeight, controlValueText, 'rounded-lg px-3 py-2'),
        compact: cn(
          controlHeightCompact,
          controlValueTextCompact,
          'gap-x-1 rounded-md px-2.5'
        ),
      },
    },
    defaultVariants: {
      density: 'comfortable',
    },
  }
);

// Rows drop `py-2` (36px) → `py-1.5` (32px). Deliberately defined apart from
// `menuItemVariants` — a listbox row is not a menu action; if listbox styling
// needs sharing later it gets its own recipe — while mirroring the menus'
// visual language: cursor-pointer rows, `rounded-md` (8) at both densities,
// concentric with the `rounded-lg` (12) content across the `p-1` (4) gutter.
//
// Neither density states a weight, matching `menuItemVariants`: a row is a value,
// so it inherits 400 at both steps and can't outweigh its own trigger.
export const selectItemVariantsExperimental = cva(
  'relative flex w-full cursor-pointer select-none items-center gap-2 outline-none hover:bg-surface-hover hover:text-text-primary focus:bg-surface-hover focus:text-text-primary active:bg-surface-pressed data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      density: {
        comfortable: "rounded-md py-1.5 text-sm [&_svg:not([class*='size-'])]:size-4",
        compact: "rounded-md py-1 text-xs [&_svg:not([class*='size-'])]:size-3.5",
      },
    },
    defaultVariants: {
      density: 'comfortable',
    },
  }
);

/** Drops the hardcoded `opacity-50`, which would compound with `controlChevron`'s to 25%. */
export const selectChevronClassesExperimental = 'opacity-100';

/** Menu heading — takes the menu recipe's 12/500. Diverges from stable's 12/400 until flag cleanup. */
export const selectLabelClassesExperimental = menuLabelClasses;

/** Semantic divider token (base uses `bg-background-secondary`). */
export const selectSeparatorClassesExperimental = menuSeparatorClasses;
